// Green shades for top ratings
const greenShades = [
  "#006400", // Darkest
  "#228B22",
  "#32CD32",
  "#7CFC00",
  "#53bf79"  // Lightest
];

/**
 * Returns a mapping of the top five highest average rates to their green shades.
 * @param {Object} averages - { rateName: value }
 * @param {string[]} rateColumns - list of rate column names
 * @returns {Object} - { rateName: color }
 */
export function getTopFiveGreenShades(averages, rateColumns) {
  const rateValues = rateColumns
    .map(rate => ({ rate, value: averages[rate] || 0 }));
  rateValues.sort((a, b) => b.value - a.value);
  const topFive = rateValues.slice(0, 5);
  const shades = {};
  topFive.forEach((item, idx) => {
    shades[item.rate] = greenShades[idx];
  });
  return shades;
}

/**
 * Returns the top 5 and bottom 5 ratings for a player
 * @param {Object} player - player object with rating values
 * @param {string[]} rateColumns - list of rate column names
 * @returns {Object} - { topRatings: Set, bottomRatings: Set }
 */
export function getPlayerRatingRanks(player, rateColumns) {
  const rateValues = rateColumns
    .map(rate => ({ rate, value: player[rate] || 0 }))
    .sort((a, b) => b.value - a.value);
  
  const topRatings = new Set(rateValues.slice(0, 5).map(item => item.rate));
  const bottomRatings = new Set(rateValues.slice(-5).map(item => item.rate));
  
  return { topRatings, bottomRatings };
}

/**
 * Returns both top 5 and bottom 5 ratings for lineup averages
 * @param {Object} averages - { rateName: value }
 * @param {string[]} rateColumns - list of rate column names
 * @returns {Object} - { topRatings: Set, bottomRatings: Set }
 */
export function getAverageRatingRanks(averages, rateColumns) {
  const rateValues = rateColumns
    .map(rate => ({ rate, value: averages[rate] || 0 }))
    .sort((a, b) => b.value - a.value);
  
  const topRatings = new Set(rateValues.slice(0, 5).map(item => item.rate));
  const bottomRatings = new Set(rateValues.slice(-5).map(item => item.rate));
  
  return { topRatings, bottomRatings };
}

/**
 * Creates a color scale from green (high) to red (low) based on rating values
 * @param {number} value - The rating value
 * @param {number} min - Minimum rating value in the dataset
 * @param {number} max - Maximum rating value in the dataset
 * @returns {string} - CSS color string
 */
export function getRatingColor(value, min, max) {
  if (min === max) return '#666666'; // Default gray if all values are the same
  
  // Normalize value to 0-1 scale
  const normalized = (value - min) / (max - min);
  
  // Create color scale from red (0) to green (1)
  const red = Math.round(255 * (1 - normalized));
  const green = Math.round(255 * normalized);
  const blue = 0;
  
  return `rgb(${red}, ${green}, ${blue})`;
}

/**
 * Gets the min and max values for all ratings in a dataset
 * @param {Object[]} players - Array of player objects
 * @param {string[]} rateColumns - Array of rating column names
 * @returns {Object} - { min: number, max: number }
 */
export function getRatingRange(players, rateColumns) {
  let min = Infinity;
  let max = -Infinity;
  
  players.forEach(player => {
    rateColumns.forEach(rate => {
      const value = player[rate] || 0;
      if (value < min) min = value;
      if (value > max) max = value;
    });
  });
  
  return { min, max };
}

/**
 * Maps a position code to a display string.
 * @param {string} code - e.g. "C", "L", "R", "D"
 * @param {Object} positionMap - e.g. { C: "Center", ... }
 * @returns {string}
 */
export function codeToDisplay(code, positionMap) {
  return positionMap[code] || code;
}

/**
 * Calculate averages for a given lineup.
 * @param {Object[]} lineup - array of player objects
 * @param {string[]} rateColumns - list of rate column names
 * @returns {Object} - { rateName: avg }
 */
export function getLineupAverages(lineup, rateColumns) {
  if (lineup.length === 0) return {};
  const averages = {};
  rateColumns.forEach(rate => {
    const sum = lineup.reduce((acc, p) => acc + ((p && p[rate]) || 0), 0);
    averages[rate] = Math.round(sum / lineup.length);
  });
  return averages;
}

/**
 * Gets the min and max values for all ratings in a goalie dataset
 * @param {Object[]} goalies - Array of goalie objects
 * @param {string[]} rateColumns - Array of rating column names
 * @returns {Object} - { min: number, max: number }
 */
export function getGoalieRatingRange(goalies, rateColumns) {
  let min = Infinity;
  let max = -Infinity;
  
  goalies.forEach(goalie => {
    rateColumns.forEach(rate => {
      const value = (goalie && goalie[rate]) || 0;
      if (value < min) min = value;
      if (value > max) max = value;
    });
  });
  
  return { min, max };
}

/**
 * Get all players in a line (forwards or defense).
 * @param {Object} line - { LW: player, C: player, RW: player } or { D1: player, D2: player }
 * @returns {Object[]} - array of player objects (not null)
 */
export function getLinePlayers(line) {
  return Object.values(line).filter(p => p != null); // Filters both null and undefined
}
